import React from 'react';
import { makeStyles } from "@material-ui/core/styles";
import { LocationOn, MyLocation } from "@material-ui/icons";

// 🎨 Estilos para o mapa SVG minimalista
const useMinimalistMapStyles = makeStyles((theme) => ({
  mapContainer: {
    position: 'relative',
    width: '100%',
    height: '140px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    }
  },
  mapSvg: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  pinContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2,
    animation: '$bounce 2s infinite',
  },
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translate(-50%, -50%) translateY(0)',
    },
    '40%': {
      transform: 'translate(-50%, -50%) translateY(-8px)',
    },
    '60%': {
      transform: 'translate(-50%, -50%) translateY(-4px)',
    },
  },
  locationPin: {
    color: '#e53935',
    fontSize: '2.5rem',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
  },
  coordinatesOverlay: {
    position: 'absolute',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    zIndex: 3,
  },
}));

/**
 * 🗺️ MinimalistMap - Componente de mapa SVG criativo e minimalista
 * 
 * Cria um "mapa fake" visualmente atrativo que simula:
 * - Grid de ruas realístico
 * - Gradientes que lembram topografia
 * - Pin animado de localização
 * - Coordenadas sobreposta
 * - Efeitos hover suaves
 * 
 * @param {Object} coordinates - {lat, lng} ou null
 * @param {Function} onClick - Callback para clique no mapa
 */
const MinimalistMap = ({ coordinates, onClick }) => {
  const classes = useMinimalistMapStyles();

  // 🎯 Gerar padrão de ruas baseado nas coordenadas
  const generateStreetPattern = (lat, lng) => {
    if (!lat || !lng) return { seed: 12345, pattern: 'basic' };
    
    // Usar coordenadas como seed para padrão consistente
    const seed = Math.abs(Math.floor(lat * lng * 1000)) % 10000;
    const pattern = seed % 3 === 0 ? 'grid' : seed % 3 === 1 ? 'organic' : 'mixed';
    
    return { seed, pattern };
  };

  const { seed, pattern } = generateStreetPattern(
    coordinates?.lat, 
    coordinates?.lng
  );

  // 🎨 Componente SVG do mapa
  const renderMapSVG = () => (
    <svg className={classes.mapSvg} viewBox="0 0 350 140" preserveAspectRatio="xMidYMid slice">
      {/* 🎯 Definições e padrões */}
      <defs>
        {/* Gradiente de fundo (simula terreno) */}
        <linearGradient id={`terrainGradient-${seed}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#e8f5e8" />
          <stop offset="30%" stopColor="#f0f8f0" />
          <stop offset="70%" stopColor="#e0f2e0" />
          <stop offset="100%" stopColor="#d4f1d4" />
        </linearGradient>

        {/* Gradiente urbano */}
        <radialGradient id={`urbanGradient-${seed}`} cx="50%" cy="50%" r="70%">
          <stop offset="0%" stopColor="#f8f9fa" />
          <stop offset="60%" stopColor="#f1f3f4" />
          <stop offset="100%" stopColor="#e8eaed" />
        </radialGradient>

        {/* Padrão de ruas - Grid */}
        <pattern id={`streetGrid-${seed}`} patternUnits="userSpaceOnUse" width="25" height="25">
          <rect width="25" height="25" fill="none"/>
          <path d="M 25 0 L 0 0 0 25" fill="none" stroke="#d0d7de" strokeWidth="1" opacity="0.8"/>
          <path d="M 12.5 0 L 12.5 25 M 0 12.5 L 25 12.5" fill="none" stroke="#e7ecf0" strokeWidth="0.5" opacity="0.6"/>
        </pattern>

        {/* Padrão de ruas - Orgânico */}
        <pattern id={`streetOrganic-${seed}`} patternUnits="userSpaceOnUse" width="30" height="30">
          <rect width="30" height="30" fill="none"/>
          <path d="M 0 15 Q 7 8 15 15 T 30 15" fill="none" stroke="#d0d7de" strokeWidth="1.2" opacity="0.7"/>
          <path d="M 15 0 Q 8 7 15 15 T 15 30" fill="none" stroke="#d0d7de" strokeWidth="1.2" opacity="0.7"/>
          <circle cx="15" cy="15" r="1.5" fill="#c7d2d8" opacity="0.6"/>
          <circle cx="7" cy="7" r="0.8" fill="#d1dce0" opacity="0.4"/>
          <circle cx="23" cy="23" r="0.8" fill="#d1dce0" opacity="0.4"/>
        </pattern>

        {/* Padrão misto */}
        <pattern id={`streetMixed-${seed}`} patternUnits="userSpaceOnUse" width="28" height="28">
          <rect width="28" height="28" fill="none"/>
          <path d="M 28 0 L 0 0 0 28" fill="none" stroke="#d0d7de" strokeWidth="1" opacity="0.8"/>
          <path d="M 14 0 Q 10 14 14 28" fill="none" stroke="#e7ecf0" strokeWidth="0.8" opacity="0.6"/>
          <path d="M 0 14 Q 14 10 28 14" fill="none" stroke="#e7ecf0" strokeWidth="0.8" opacity="0.6"/>
          <path d="M 7 7 L 21 21 M 21 7 L 7 21" fill="none" stroke="#f1f3f4" strokeWidth="0.4" opacity="0.3"/>
        </pattern>

        {/* Sombra do pin */}
        <filter id={`pinShadow-${seed}`}>
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.3)"/>
        </filter>

        {/* Gradiente para pin */}
        <linearGradient id={`pinGradient-${seed}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ef5350" />
          <stop offset="100%" stopColor="#c62828" />
        </linearGradient>
      </defs>

      {/* 🌍 Fundo do terreno */}
      <rect width="350" height="140" fill={seed % 2 === 0 ? `url(#terrainGradient-${seed})` : `url(#urbanGradient-${seed})`}/>

      {/* 🛣️ Camada de ruas */}
      <rect 
        width="350" 
        height="140" 
        fill={`url(#street${pattern === 'grid' ? 'Grid' : pattern === 'organic' ? 'Organic' : 'Mixed'}-${seed})`}
      />

      {/* 🏗️ Elementos decorativos baseados no seed */}
      {seed % 4 === 0 && (
        <g opacity="0.4">
          {/* Simula quarteirões residenciais */}
          <rect x="50" y="30" width="40" height="25" fill="#f0f0f0" stroke="#e0e0e0" strokeWidth="0.5" rx="2"/>
          <rect x="200" y="60" width="35" height="30" fill="#f5f5f5" stroke="#e0e0e0" strokeWidth="0.5" rx="2"/>
          <rect x="120" y="80" width="50" height="20" fill="#f0f0f0" stroke="#e0e0e0" strokeWidth="0.5" rx="2"/>
          {/* Pequenos detalhes */}
          <rect x="55" y="35" width="8" height="6" fill="#e8e8e8" opacity="0.7"/>
          <rect x="205" y="65" width="6" height="8" fill="#e8e8e8" opacity="0.7"/>
        </g>
      )}

      {seed % 4 === 1 && (
        <g opacity="0.5">
          {/* Simula área comercial */}
          <rect x="80" y="40" width="60" height="35" fill="#fafafa" stroke="#e0e0e0" strokeWidth="0.7" rx="3"/>
          <rect x="250" y="25" width="45" height="40" fill="#f8f8f8" stroke="#e0e0e0" strokeWidth="0.7" rx="3"/>
          <rect x="30" y="90" width="70" height="25" fill="#fafafa" stroke="#e0e0e0" strokeWidth="0.7" rx="3"/>
          {/* Detalhes comerciais */}
          <circle cx="110" cy="57" r="2" fill="#ddd" opacity="0.8"/>
          <circle cx="272" cy="45" r="1.5" fill="#ddd" opacity="0.8"/>
        </g>
      )}

      {/* 🌳 Elementos verdes (parques/praças) */}
      {seed % 3 === 1 && (
        <g opacity="0.6">
          <circle cx="80" cy="50" r="12" fill="#c8e6c9" stroke="#a5d6a7" strokeWidth="0.5"/>
          <circle cx="270" cy="90" r="8" fill="#c8e6c9" stroke="#a5d6a7" strokeWidth="0.5"/>
          <rect x="160" y="25" width="25" height="15" fill="#c8e6c9" stroke="#a5d6a7" strokeWidth="0.5" rx="3"/>
          {/* Árvores pequenas */}
          <circle cx="85" cy="45" r="2" fill="#81c784" opacity="0.8"/>
          <circle cx="75" cy="55" r="1.5" fill="#81c784" opacity="0.8"/>
          <circle cx="275" cy="85" r="1" fill="#81c784" opacity="0.8"/>
        </g>
      )}

      {/* 💧 Elemento água (rio/lago) */}
      {seed % 5 === 2 && (
        <g opacity="0.7">
          <path 
            d="M 0 100 Q 70 90 140 105 T 350 95 L 350 140 L 0 140 Z" 
            fill="rgba(33, 150, 243, 0.2)" 
            stroke="rgba(33, 150, 243, 0.3)" 
            strokeWidth="0.5"
          />
          {/* Ondulações na água */}
          <path d="M 50 105 Q 80 100 110 105 T 170 105" fill="none" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="0.3"/>
          <path d="M 200 98 Q 230 93 260 98 T 320 98" fill="none" stroke="rgba(33, 150, 243, 0.4)" strokeWidth="0.3"/>
        </g>
      )}

      {/* 🚗 Elementos de trânsito */}
      {seed % 6 === 3 && (
        <g opacity="0.3">
          <rect x="100" y="68" width="15" height="4" fill="#666" rx="2"/>
          <rect x="180" y="35" width="4" height="15" fill="#666" rx="2"/>
          <rect x="220" y="85" width="12" height="3" fill="#888" rx="1.5"/>
        </g>
      )}

      {/* 🎯 Círculo sutil no centro (onde fica o pin) */}
      <circle 
        cx="175" 
        cy="70" 
        r="25" 
        fill="none" 
        stroke="rgba(229, 57, 53, 0.2)" 
        strokeWidth="1" 
        strokeDasharray="3,3"
        opacity="0.7"
      />

      {/* 🎯 Círculo interno menor */}
      <circle 
        cx="175" 
        cy="70" 
        r="12" 
        fill="none" 
        stroke="rgba(229, 57, 53, 0.3)" 
        strokeWidth="0.5" 
        strokeDasharray="2,2"
        opacity="0.5"
      />
    </svg>
  );

  return (
    <div className={classes.mapContainer} onClick={onClick}>
      {/* 🗺️ SVG do mapa */}
      {renderMapSVG()}

      {/* 📍 Pin de localização animado */}
      <div className={classes.pinContainer}>
        <LocationOn className={classes.locationPin} style={{ filter: `url(#pinShadow-${seed})` }} />
      </div>

      {/* 📊 Coordenadas sobrepostas */}
      {coordinates && (
        <div className={classes.coordinatesOverlay}>
          {coordinates.lat}, {coordinates.lng}
        </div>
      )}
    </div>
  );
};

export default MinimalistMap;
